import { lazy, Suspense, useEffect } from 'react'

import { useTranslation } from 'react-i18next'
import classnames from 'classnames'

import Loading from 'common/components/loading'
import Icons from 'common/components/icons'
import PoweredBy from 'common/components/poweredBy'
import LeadMessage from 'modules/transmission/components/leadMessage'
import GtagHelper, { Subsection } from 'common/helpers/gtag'
import ConfirmLead from 'modules/lead/components/confirmLead'
import ConfirmLeadTitle from 'modules/lead/components/confirmLeadTitle'
import { useLeadContext } from 'modules/lead/context'
import { useTransmissionContext } from 'modules/transmission/context'
import useLeadLogin from 'modules/lead/hooks/useLeadLogin'
import { useAuth } from 'modules/auth/hooks/useAuth'

const FormNewLead = lazy(() => import('modules/lead/components/formNewLead'))

export interface Props {
  registered: boolean
}

const LivePreview = ({ registered }: Props) => {
  const { t } = useTranslation()
  const { leadVariant, leadData: { showConfirm, ...leadData } } = useLeadContext()
  const { isLogged } = useAuth()
  const { transmissionData } = useTransmissionContext()
  const { confirmLogin, clearLogin, handleAnonymousFormSubmit } = useLeadLogin()

  useEffect(() => {
    GtagHelper.sendPageView({
      subsection: Subsection.PRE_LIVE,
      logged: false
    })
  }, [])

  const renderLeadMessage = () => {
    if (!leadVariant) {
      return null
    }

    return (
      <div data-test="lead-message-container" className="message-container">
        <LeadMessage variant={leadVariant} maxParticipants={transmissionData.maxParticipants} />
      </div>
    )
  }

  const renderFormFields = () => {
    return (
      <div className="form-fields" data-test="form-new-lead">
        <h1>{t('live-preview.form.with_restrictions.title')}</h1>
        <h2>{t('live-preview.form.with_restrictions.subtitle')}</h2>
        <Suspense fallback={<Loading />}>
          <FormNewLead
            onSubmit={handleAnonymousFormSubmit}
            disabled={!transmissionData.code}/>
        </Suspense>
      </div>
    )
  }

  const renderBlockMessage = () => {
    return (
      <div className="block-mask" data-test="block-message">
        <div className="block-label">
          <div className="icon">
            <Icons prefix="fas" iconName="lock" className="icon-lock" />
          </div>
          <div className="description">
            {t('live-preview.thumb-block-label')}
          </div>
        </div>
      </div>
    )
  }

  const renderContent = () => {
    if (!isLogged() && !registered) {
      return renderFormFields()
    }

    if (isLogged() && showConfirm) {
      return (
        <ConfirmLead
          name={leadData.data.name}
          onConfirm={confirmLogin}
          onCancel={clearLogin} />
      )
    }

    return renderLeadMessage()
  }

  return (
    <div className="live-preview-container" data-test="live-preview">
      <div
        className={classnames('form-container', { 'confirm-container': showConfirm })}
        data-test="form-container">
        <div className="form-body">
          {transmissionData.largeThumbnailLink && (
            <div className="form-thumb">
              <img
                src={transmissionData.largeThumbnailLink}
                alt={t('general.transmission_image')}
                data-test="form-thumb" />
            </div>
          )}
          <ConfirmLeadTitle showConfirm={showConfirm} transmission={transmissionData} />
          {renderContent()}
        </div>
        {!showConfirm && <PoweredBy />}
      </div>
      <div className="preview-container">
        {transmissionData?.largeThumbnailLink && (
          <div className="live-thumbnail">
            <img
              src={transmissionData?.largeThumbnailLink}
              alt={t('general.transmission_image')}
              data-test="live-thumb" />
            {!registered && renderBlockMessage()}
          </div>
        )}
        <span className="live-description">{transmissionData?.description}</span>
      </div>
    </div>
  )
}

export default LivePreview
